import React, {useContext, useEffect, useState} from "react";
import CardContent from "@material-ui/core/CardContent";
import {makeStyles} from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Cart from "./Cart";
import RecaptchaGate from "./RecaptchaGate";
import {isCheckoutVerified} from "../../utils/checkoutVerification";
import Grid from "@material-ui/core/Grid";
import {Divider} from "@material-ui/core";
import {CartContext} from "../../contexts/CartContext";
import CircularProgress from "@material-ui/core/CircularProgress";
import {FormContext} from "../../contexts/FormContext";
import DonationSetting from "../DonationSetting/DonationSetting";
import * as ReactGA from "react-ga";
import {Alert} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import {UPDATE_PRODUCT_ACTIVITY_IN_PROCESS_MUTATION} from "./UpdateProductActivityInProcessMutation.mutation";
import {useHistory} from "react-router-dom";

const CheckoutForm = (props) => {
    const venue = window.location.pathname.split("/")[1]
    const form = window.location.pathname.split("/")[2]

    const cartContext = useContext(CartContext);
    const formContext = useContext(FormContext);
    const history = useHistory();
    const needsVerification = formContext.form?.enableRecaptcha === true &&
        formContext.form?.venue?.paymentProcessor !== 'payroc_hosted_fields' &&
        !isCheckoutVerified(formContext.checkoutVerification, formContext.form, cartContext.custref);
    const skipCheckout = props.skipCheckout && !needsVerification;
    const formStatus = {
        NOT_SUBMITTED: 'not submitted',
        PROCESSING: 'processing',
        SUCCESS: 'success',
        FAILURE: 'failure'
    }

    const [currentFormStatus, setCurrentFormStatus] = useState(formStatus.NOT_SUBMITTED);
    const [checkoutForm, setCheckoutForm] = useState();
    const [custRefInput, setCustRefInput] = useState();
    const [worldnetHash, setWorldnetHash] = useState('');
    const [worldnetDatetime, setWorldnetDatetime] = useState('');
    const [worldnetAmountCents, setWorldnetAmountCents] = useState('');
    const [worldnetOrderId, setWorldnetOrderId] = useState('');

    const [bridgepayAuthToken, setBridgepayAuthToken] = useState('');
    const [bridgepayTokenLoading, setBridgepayTokenLoading] = useState(false);

    const [productsLimitReached, setProductsLimitReached] = useState([]);

    const successURL = () => {
        return process.env.REACT_APP_SUBMIT_URL + "/" + venue + "/" + form + "/success"
    }

    const webhookURL = () => {
        return process.env.REACT_APP_SUBMIT_URL + "/webhook/success"
    }

    const failureURL = () => {
        return process.env.REACT_APP_SUBMIT_URL + "/" + venue + "/" + form + "/failure"
    }

    const sendGoogleAnalytics = () => {
        if (formContext.form.googleAnaltyicsId === undefined) {
            return
        }

        ReactGA.initialize(formContext.form.googleAnaltyicsId);
        ReactGA.plugin.require('ecommerce')
        Object.keys(cartContext.products).forEach(key => (
            ReactGA.plugin.execute('ecommerce', 'addProduct', {
                id: key,        // Transaction ID. Required.
                name: cartContext.products[key].name,          // Product name. Required.
                sku: key,                 // SKU/code.
                category: '',         // Category or variation.
                price: cartContext.products[key].price.toFixed(2),                 // Unit price.
                quantity: cartContext.products[key].quantity,                 // Quantity.
            })
        ))

        if (cartContext.donation) {
            ReactGA.plugin.execute('ecommerce', 'addProduct', {
                id: "donation",        // Transaction ID. Required.
                name: (cartContext.donation.name !== "") ? cartContext.donation.name : "Donation",          // Product name. Required.
                sku: "donation",                 // SKU/code.
                category: '',         // Category or variation.
                price: cartContext.donation.price.toFixed(2),                 // Unit price.
                quantity: cartContext.donation.quantity,                 // Quantity.
            })
        }

        if (cartContext.transactionFee) {
            ReactGA.plugin.execute('ecommerce', 'addProduct', {
                id: "transactionFee",        // Transaction ID. Required.
                name: cartContext.transactionFee.label,          // Product name. Required.
                sku: "transactionFee",                 // SKU/code.
                category: '',         // Category or variation.
                price: cartContext.transactionFee.amount.toFixed(2),                 // Unit price.
                quantity: 1,                 // Quantity.
            })
        }

        ReactGA.plugin.execute('ec', 'setAction', 'purchase', {
            id: cartContext.custref,
            revenue: cartContext.total,
        });
        ReactGA.plugin.execute('ecommerce', 'send');
        ReactGA.plugin.execute('ecommerce', 'clear');
    }

    const useStyles = makeStyles((theme) => ({
        root: {
            margin: "auto",
            boxShadow: "0 8px 40px -12px rgba(0,0,0,0.3)",
            "&:hover": {
                boxShadow: "0 16px 70px -12.125px rgba(0,0,0,0.3)"
            },
        },
        field: {
            width: "100%"
        },
        payButton: {
            marginTop: '30px'
        },
        creditCardImages: {},
        label: {
            padding: '15px 0'
        }
    }));

    const classes = useStyles();

    function uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    useEffect(() => {
        if (!formContext.form?.enableRecaptcha || !cartContext.custref) cartContext.setCustRef(uuidv4());
    }, [])

    // Worldnet test mode credentials
    const WORLDNET_TEST_TERMINAL_ID = '6025001';
    const WORLDNET_TEST_TERMINAL_SECRET = 'Damageless2025!Gessel';

    // Helper to get the appropriate terminal ID (test or production)
    // Fallback chain: test mode -> recurring terminal -> non-recurring terminal -> venue legacy
    const getWorldnetTerminalId = () => {
        if (formContext.form?.testMode && formContext.form?.venue?.paymentProcessor === 'worldnet') {
            return WORLDNET_TEST_TERMINAL_ID;
        }
        if (cartContext.isRecurring && formContext.form?.recurringTerminalId?.terminal_id) {
            return formContext.form.recurringTerminalId.terminal_id;
        }
        if (formContext.form?.nonRecurringTerminalId?.terminal_id) {
            return formContext.form.nonRecurringTerminalId.terminal_id;
        }
        return formContext.form?.venue?.worldnet_terminal_id;
    };

    // Helper to get the appropriate terminal secret (test or production)
    // Fallback chain: test mode -> recurring terminal -> non-recurring terminal -> venue legacy
    const getWorldnetTerminalSecret = () => {
        if (formContext.form?.testMode && formContext.form?.venue?.paymentProcessor === 'worldnet') {
            return WORLDNET_TEST_TERMINAL_SECRET;
        }
        if (cartContext.isRecurring && formContext.form?.recurringTerminalId?.terminal_secret) {
            return formContext.form.recurringTerminalId.terminal_secret;
        }
        if (formContext.form?.nonRecurringTerminalId?.terminal_secret) {
            return formContext.form.nonRecurringTerminalId.terminal_secret;
        }
        return formContext.form?.venue?.worldnet_terminal_secret;
    };

    // Calculate Worldnet hash when form data changes
    useEffect(() => {
        const calculateWorldnetHash = async () => {
            if (formContext.form?.venue?.paymentProcessor === 'worldnet' && cartContext.custref) {
                try {
                    const datetime = formContext.generateWorldnetDatetime();
                    setWorldnetDatetime(datetime);

                    const terminalId = getWorldnetTerminalId();
                    const shortOrderId = formContext.generateShortOrderId(cartContext.custref);
                    setWorldnetOrderId(shortOrderId);

                    // Store the mapping of short order ID to custref for webhook processing
                    const orderMapping = {
                        orderId: shortOrderId,
                        custref: cartContext.custref,
                        venue: window.location.pathname.split("/")[1],
                        form: window.location.pathname.split("/")[2],
                        timestamp: new Date().toISOString()
                    };

                    // Store in localStorage as backup
                    localStorage.setItem(`worldnet_order_${shortOrderId}`, JSON.stringify(orderMapping));
                    console.log('Stored order mapping locally:', orderMapping);

                    // Store in database via API
                    try {
                        const response = await fetch(process.env.REACT_APP_SUBMIT_URL + "/store-order-mapping", {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(orderMapping)
                        });

                        if (response.ok) {
                            console.log('Order mapping stored successfully in database');
                        } else {
                            console.error('Failed to store order mapping in database');
                        }
                    } catch (error) {
                        console.error('Error storing order mapping:', error);
                        // Continue processing even if database storage fails
                    }

                    const currency = formContext.form.venue.worldnet_currency || "USD";
                    const amountForHash = cartContext.total.toFixed(2); // Decimal format for hash
                    const amountForForm = Math.round(cartContext.total * 100); // Cents for form field
                    setWorldnetAmountCents(amountForForm);
                    const secret = getWorldnetTerminalSecret();

                    // Generate hash using correct Worldnet format (single currency by default)
                    const hashString = formContext.generateWorldnetHashString(
                        terminalId, shortOrderId, currency, amountForHash, datetime, successURL(), webhookURL(), secret, false
                    );

                    const hash = await formContext.generateSHA512Hash(hashString);
                    setWorldnetHash(hash);
                } catch (error) {
                    console.error('Failed to generate Worldnet hash:', error);
                    setWorldnetHash('HASH_ERROR');
                }
            }
        };

        calculateWorldnetHash();
    }, [formContext.form, cartContext.custref, cartContext.total, cartContext.isRecurring])

    // Fetch BridgePay auth token and store mapping when form loads
    useEffect(() => {
        const setupBridgepay = async () => {
            if (formContext.form?.venue?.paymentProcessor === 'bridgepay' && cartContext.custref) {
                setBridgepayTokenLoading(true);
                try {
                    // Fetch auth token
                    const token = await formContext.fetchBridgepayAuthToken();
                    setBridgepayAuthToken(token || '');

                    // Store custref to venue/form mapping for redirect lookup
                    await fetch(process.env.REACT_APP_SUBMIT_URL + "/store-bridgepay-mapping", {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            custref: cartContext.custref,
                            venue: window.location.pathname.split("/")[1],
                            form: window.location.pathname.split("/")[2]
                        })
                    });
                    console.log('Stored BridgePay mapping for custref:', cartContext.custref);
                } catch (error) {
                    console.error('BridgePay setup error:', error);
                } finally {
                    setBridgepayTokenLoading(false);
                }
            }
        };
        setupBridgepay();
    }, [formContext.form, cartContext.custref]);

    const [addProductActivityInProcess, {loading, error, data}] = useMutation(UPDATE_PRODUCT_ACTIVITY_IN_PROCESS_MUTATION, {
        fetchPolicy: "network-only",
        variables: {
            product: "",
            quantity: 0
        },
    });

    const [mustSelectProduct, setMustSelectProduct] = useState(false);

    useEffect(() => {
        let products_filtered = Object.keys(cartContext.products).filter(key => key !== "form-fee")
        let products = Object.keys(cartContext.products)
        if (products_filtered.length === 0 && products_filtered.length !== products.length && !cartContext.donation) {
            setMustSelectProduct(true);
        }

        Object.keys(cartContext.products).forEach(key => {
            let inProcess = 0;

            if (!cartContext.products[key].quantityAvailable) {
                setProductsLimitReached([]);
                return
            }

            if (cartContext.products[key].product_activity_in_processes) {
                cartContext.products[key].product_activity_in_processes.forEach((process) => {
                    inProcess += process.quantity
                })
            }

            if ((cartContext.products[key].quantityAvailable - inProcess - cartContext.products[key].quantity - cartContext.products[key].amountSold) < 0) {
                setProductsLimitReached([...productsLimitReached, cartContext.products[key]])
            }

            addProductActivityInProcess({
                variables: {
                    product: key,
                    quantity: cartContext.products[key].quantity
                }
            })
        })
    }, [])

    // Auto-submit when skipCheckout is true and payment form is ready
    useEffect(() => {
        if (!skipCheckout) return;
        if (!cartContext.custref) return;

        const processor = formContext.form?.venue?.paymentProcessor;

        // Hosted fields requires user interaction with iframes — skip auto-submit
        if (processor === 'payroc_hosted_fields') return;

        if (!checkoutForm) return;

        // Wait for processor-specific readiness
        if (processor === 'worldnet' && (!worldnetHash || worldnetHash === 'HASH_ERROR')) return;
        if (processor === 'bridgepay' && (bridgepayTokenLoading || !bridgepayAuthToken)) return;

        // All ready — auto-submit
        submitForm();
    }, [skipCheckout, checkoutForm, cartContext.custref, worldnetHash, bridgepayAuthToken, bridgepayTokenLoading]);

    const submitForm = async function () {
        if (needsVerification) return false;
        sendGoogleAnalytics()
        if (cartContext.total === 0 && !formContext.isDataCollectionFormType()) {
            alert("Please select a product or make a donation to continue. ")
            return false
        }

        if (cartContext.hasPhysicalItems()) {
            const addr = formContext.formData["patronAddress"];
            const zip = formContext.formData["patronZip"];
            if (!addr || !zip) {
                alert("A shipping address is required for physical items in your cart.");
                return false;
            }
        }

        try {
            await props.handleFormSubmission();
            checkoutForm.submit();
        } catch (error) {
            alert(error.message);
        }
    }

    switch (currentFormStatus) {
        case formStatus.PROCESSING:
            return (
                <CircularProgress/>
            )
        case formStatus.SUCCESS:
            return (
                <div className="success_block" dangerouslySetInnerHTML={{__html: formContext.form.successHTML}}/>
            )
        case formStatus.FAILURE:
            return (
                <div className="success_block" dangerouslySetInnerHTML={{__html: formContext.form.failureHTML}}/>
            )
        case formStatus.NOT_SUBMITTED:
        default:
            if (skipCheckout) {
                // Payroc hosted fields — redirect to standalone payment page
                if (formContext.form?.venue?.paymentProcessor === 'payroc_hosted_fields') {
                    history.push(`/${venue}/${form}/payment`);
                    return (<div className="block"><CircularProgress/></div>);
                }
                // Render only the hidden payment form + spinner for auto-submit
                return (<div className="block">
                    <CircularProgress/>
                    <form id="checkout-form" ref={setCheckoutForm} action={formContext.paymentUrl()} method={formContext.form?.venue?.paymentProcessor === 'bridgepay' ? 'get' : 'post'} style={{display: 'none'}}>
                        {formContext.form?.venue?.paymentProcessor === 'bridgepay' ? (
                            <>
                                <input type="hidden" value={cartContext.total.toFixed(2)} name="amount"/>
                                <input type="hidden" value={bridgepayAuthToken} name="authToken"/>
                                <input type="hidden" value={cartContext.custref} name="InvoiceNumber"/>
                                <input type="hidden" value={cartContext.custref} name="customerAccountCode"/>
                                <input type="hidden" value={formContext.currentBuyer()["email"]} name="emailAddress"/>
                            </>
                        ) : formContext.form?.venue?.paymentProcessor === 'worldnet' ? (
                            <>
                                <input type="hidden" value={getWorldnetTerminalId()} name="TERMINALID"/>
                                <input type="hidden" value={formContext.currentBuyer()["name"]} name="CARDHOLDERNAME"/>
                                <input type="hidden" value={worldnetOrderId} name="ORDERID"/>
                                <input type="hidden" value={cartContext.total.toFixed(2)} name="AMOUNT"/>
                                <input type="hidden" value={worldnetDatetime} name="DATETIME"/>
                                <input type="hidden" value={formContext.form.venue.worldnet_currency || "USD"} name="CURRENCY"/>
                                <input type="hidden" value={successURL()} name="RECEIPTPAGEURL"/>
                                <input type="hidden" value={webhookURL()} name="VALIDATIONURL"/>
                                <input type="hidden" value={worldnetOrderId} name="SECURECARDMERCHANTREF"/>
                                <input type="hidden" value="Y" name="AUTOREADY"/>
                                <input type="hidden" value={worldnetHash} name="HASH"/>
                                {cartContext.isRecurring && (
                                    <>
                                        <input type="hidden" value="UNSCHEDULED" name="STOREDCREDENTIALUSE"/>
                                        <input type="hidden" value="FIRST_TXN" name="STOREDCREDENTIALTXTYPE"/>
                                    </>
                                )}
                                <input type="hidden" value={formContext.currentBuyer()["address"]} name="ADDRESS1"/>
                                <input type="hidden" value={formContext.currentBuyer()["city"]} name="CITY"/>
                                <input type="hidden" value={formContext.currentBuyer()["region"]} name="REGION"/>
                                <input type="hidden" value={formContext.currentBuyer()["country"]} name="COUNTRY"/>
                                <input type="hidden" value={formContext.currentBuyer()["zip"]} name="POSTCODE"/>
                            </>
                        ) : (
                            <>
                                <input type="hidden" value={cartContext.total} name="amount"/>
                                <input type="hidden" value={formContext.currentBuyer()["name"]} name="cardholdername"/>
                                <input type="hidden" value={formContext.currentBuyer()["address"]} name="street"/>
                                <input type="hidden" value={formContext.currentBuyer()["city"]} name="city"/>
                                <input type="hidden" value={formContext.currentBuyer()["region"]} name="state"/>
                                <input type="hidden" value={formContext.currentBuyer()["country"]} name="country"/>
                                <input type="hidden" value={formContext.currentBuyer()["zip"]} name="zip"/>
                                <input type="hidden" value={formContext.currentBuyer()["email"]} name="customer_email"/>
                                <input type="hidden" value={cartContext.custref} name="custref"/>
                                <input type="hidden" value={(cartContext.donationSettings) ? cartContext.donationSettings.isSelected : false} name="coveredCosts"/>
                                <input type="hidden" value={formContext.form.name} name="comments"/>
                                <input type="hidden" value={successURL()} name="success_url"/>
                                <input type="hidden" value={webhookURL()} name="webhook_url"/>
                                <input type="hidden" value={failureURL()} name="cancel_url"/>
                                <input type="hidden" value={formContext.form.venue.webhookErrorEmail} name="webhook_error_email"/>
                                <input type="hidden" value={(formContext.form.venue.hideComments === true) ? "1" : "0"} name="hide_comments"/>
                                <input type="hidden" value={(formContext.form.venue.hideCustRef === true) ? "1" : "0"} name="hide_cust_ref"/>
                            </>
                        )}
                    </form>
                </div>);
            }
            return (<div className="block">
                    {(cartContext.donationSettings && cartContext.donationSettings.placement === "aboveShoppingCart") && (
                        <DonationSetting
                            donationSetting={cartContext.donationSettings}
                        />
                    )}
                    {(productsLimitReached.map(product => (
                        <Alert severity="error"><div id="product-unavailable" dangerouslySetInnerHTML={{__html: product.productActivityInProgressText.replace("${product}", product.name)}} /></Alert>
                    )))}
                    <CardContent>
                        <Cart/>
                        {(cartContext.donationSettings && cartContext.donationSettings.placement === "belowShoppingCart") && (
                            <DonationSetting
                                donationSetting={cartContext.donationSettings}
                            />
                        )}
                        <Divider/>
                        {formContext.form?.enableRecaptcha && formContext.form?.venue?.paymentProcessor !== 'payroc_hosted_fields' && (needsVerification
                            ? <RecaptchaGate formId={formContext.form.id} custref={cartContext.custref}
                                onVerified={formContext.setCheckoutVerification}/>
                            : <Alert severity="success">Verification complete.</Alert>)}
                        {formContext.form?.venue?.paymentProcessor === 'payroc_hosted_fields' ? (
                            <>
                                <Grid container spacing={3}>
                                    <Grid container justify="center">
                                        <Button
                                            id="submit-button"
                                            className={classes.payButton}
                                            size="large"
                                            onClick={async () => {
                                                if (cartContext.total === 0 && !formContext.isDataCollectionFormType()) {
                                                    alert("Please select a product or make a donation to continue.");
                                                    return;
                                                }
                                                if (needsVerification || productsLimitReached.length > 0 || mustSelectProduct) return;
                                                try {
                                                    await props.handleFormSubmission();
                                                    history.push(`/${venue}/${form}/payment`);
                                                } catch (error) {
                                                    alert(error.message);
                                                }
                                            }}
                                            disabled={(needsVerification || productsLimitReached.length > 0 || mustSelectProduct)}
                                            color="secondary">Proceed to Payment</Button>
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                        <form id="checkout-form" ref={setCheckoutForm} action={formContext.paymentUrl()} method={formContext.form?.venue?.paymentProcessor === 'bridgepay' ? 'get' : 'post'}>
                            {formContext.form?.venue?.paymentProcessor === 'bridgepay' ? (
                                // BridgePay HPP uses GET query string parameters
                                <>
                                    <input type="hidden" value={cartContext.total.toFixed(2)} name="amount" id="bridgepay-amount"/>
                                    <input type="hidden" value={bridgepayAuthToken} name="authToken" id="bridgepay-authToken"/>
                                    <input type="hidden" value={cartContext.custref} name="InvoiceNumber" id="bridgepay-invoiceNumber"/>
                                    <input type="hidden" value={cartContext.custref} name="customerAccountCode" id="bridgepay-customerAccountCode"/>
                                    <input type="hidden" value={formContext.currentBuyer()["email"]} name="emailAddress" id="bridgepay-emailAddress"/>
                                </>
                            ) : formContext.form?.venue?.paymentProcessor === 'worldnet' ? (
                                // Worldnet HPP fields
                                <>
                                    <input type="hidden" value={getWorldnetTerminalId()} name="TERMINALID" id="TERMINALID"/>
                                    <input type="hidden" value={formContext.currentBuyer()["name"]} name="CARDHOLDERNAME" id="CARDHOLDERNAME"/>
                                    <input type="hidden" value={worldnetOrderId} name="ORDERID" id="ORDERID"/>
                                    <input type="hidden" value={cartContext.total.toFixed(2)} name="AMOUNT" id="AMOUNT"/>
                                    <input type="hidden" value={worldnetDatetime} name="DATETIME" id="DATETIME"/>
                                    <input type="hidden" value={formContext.form.venue.worldnet_currency || "USD"} name="CURRENCY" id="CURRENCY"/>
                                    <input type="hidden" value={successURL()} name="RECEIPTPAGEURL" id="RECEIPTPAGEURL"/>
                                    <input type="hidden" value={webhookURL()} name="VALIDATIONURL" id="VALIDATIONURL"/>
                                    <input type="hidden" value={worldnetOrderId} name="SECURECARDMERCHANTREF" id="SECURECARDMERCHANTREF"/>
                                    <input type="hidden" value="Y" name="AUTOREADY" id="AUTOREADY"/>
                                    <input type="hidden" value={worldnetHash} name="HASH" id="HASH"/>
                                    {cartContext.isRecurring && (
                                        <>
                                            <input type="hidden" value="UNSCHEDULED" name="STOREDCREDENTIALUSE" id="STOREDCREDENTIALUSE"/>
                                            <input type="hidden" value="FIRST_TXN" name="STOREDCREDENTIALTXTYPE" id="STOREDCREDENTIALTXTYPE"/>
                                        </>
                                    )}
                                    {/* Address fields for AVS and fraud prevention */}
                                    <input type="hidden" value={formContext.currentBuyer()["address"]} name="ADDRESS1" id="ADDRESS1"/>
                                    <input type="hidden" value={formContext.currentBuyer()["city"]} name="CITY" id="CITY"/>
                                    <input type="hidden" value={formContext.currentBuyer()["region"]} name="REGION" id="REGION"/>
                                    <input type="hidden" value={formContext.currentBuyer()["country"]} name="COUNTRY" id="COUNTRY"/>
                                    <input type="hidden" value={formContext.currentBuyer()["zip"]} name="POSTCODE" id="POSTCODE"/>
                                </>
                            ) : (
                                // TUNL fields (existing)
                                <>
                                    <input type="hidden" value={cartContext.total} name="amount" id="amount"/>
                                    <input type="hidden" value={formContext.currentBuyer()["name"]} name="cardholdername"
                                           id="cardholdername"/>
                                    <input type="hidden" value={formContext.currentBuyer()["address"]} name="street"
                                           id="street"/>
                                    <input type="hidden" value={formContext.currentBuyer()["city"]} name="city" id="city"/>
                                    <input type="hidden" value={formContext.currentBuyer()["region"]} name="state" id="state"/>
                                    <input type="hidden" value={formContext.currentBuyer()["country"]} name="country"
                                           id="country"/>
                                    <input type="hidden" value={formContext.currentBuyer()["zip"]} name="zip" id="zip"/>
                                    <input type="hidden" value={formContext.currentBuyer()["email"]} name="customer_email"
                                           id="customer_email"/>
                                    <input type="hidden" value={cartContext.custref} ref={setCustRefInput} name="custref"
                                           id="custref"/>
                                    <input type="hidden"
                                           value={(cartContext.donationSettings) ? cartContext.donationSettings.isSelected : false}
                                           name="coveredCosts" id="coveredCosts"/>
                                    <input type="hidden" value={formContext.form.name} name="comments" id="comments"/>
                                    <input type="hidden" value={successURL()} name="success_url" id="success_url"/>
                                    <input type="hidden" value={webhookURL()} name="webhook_url" id="webhook_url"/>
                                    <input type="hidden" value={failureURL()} name="cancel_url" id="cancel_url"/>
                                    <input type="hidden" value={formContext.form.venue.webhookErrorEmail}
                                           name="webhook_error_email" id="webhook_error_email"/>
                                    <input type="hidden" value={(formContext.form.venue.hideComments === true) ? "1" : "0"}
                                           name="hide_comments" id="hide_comments"/>
                                    <input type="hidden" value={(formContext.form.venue.hideCustRef === true) ? "1" : "0"}
                                           name="hide_cust_ref" id="hide_cust_ref"/>
                                </>
                            )}
                            <Grid container spacing={3}>
                                <Grid container justify="center">
                                    <Button
                                        id="submit-button"
                                        className={classes.payButton}
                                        size="large"
                                        onClick={submitForm}
                                        disabled={(needsVerification || productsLimitReached.length > 0 || mustSelectProduct || bridgepayTokenLoading)}
                                        color="secondary">Proceed to Payment</Button>
                                </Grid>
                            </Grid>
                        </form>
                        )}

                    </CardContent>
                    {formContext.form && formContext.form.testMode && (
                        <div>
                            This Form is in TEST MODE. No credit cards will be processed. Contact Easy-Ware Support
                            before making this link public.
                            Note: Please use 999 or 9999 for the CVV and values between $5 and $8 will always be
                            declined.</div>
                    )}
                </div>
            );
    }
};

const CheckoutPage = (props) => {
    return (
        <CheckoutForm handleFormSubmission={props.handleFormSubmission} form={props.form} skipCheckout={props.skipCheckout}/>
    )
}

export default CheckoutPage;
